// `StatusInlineEditor` — US-152.
//
// Renders the current status as a pill (`<LeadStatusPill>`) and pops a
// dropdown of the 6 taxonomy values when clicked. Optimistically updates
// the cache via `useUpdateLead` so the table re-renders the new pill before
// the server round-trip resolves.
//
// Usage: rendered both in the table row (sub-cell) and the detail drawer.

import { useState } from 'react';

import { LEAD_STATUSES, type LeadStatus } from '@barel/core';
import { useUpdateLead, type LeadRow } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { LeadStatusPill, Popover, PopoverContent, PopoverTrigger } from '@barel/ui';

export interface StatusInlineEditorProps {
  lead: Pick<LeadRow, 'id' | 'status'>;
  /** Disable editing (read-only render — used in the table row). */
  readOnly?: boolean;
  /** Optional callback the parent uses to drive a toast. */
  onChanged?: (next: LeadStatus) => void;
}

export function StatusInlineEditor({ lead, readOnly = false, onChanged }: StatusInlineEditorProps) {
  const t = useT('leads');
  const [open, setOpen] = useState(false);
  const update = useUpdateLead();

  const currentLabel = t(`status.${lead.status}`);

  if (readOnly) {
    return (
      <LeadStatusPill status={lead.status} size="xs">
        {currentLabel}
      </LeadStatusPill>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="lead-status-trigger"
          className="inline-flex items-center"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <LeadStatusPill status={lead.status} size="xs">
            {currentLabel}
          </LeadStatusPill>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        data-testid="lead-status-menu"
        className="min-w-[180px] p-1"
      >
        <ul role="listbox" className="flex flex-col">
          {LEAD_STATUSES.map((option) => (
            <li key={option}>
              <button
                type="button"
                role="option"
                aria-selected={option === lead.status}
                data-testid={`lead-status-option-${option}`}
                disabled={update.isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  if (option === lead.status) return;
                  update.mutate(
                    { id: lead.id, patch: { status: option } },
                    {
                      onSuccess: () => onChanged?.(option),
                    },
                  );
                }}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-bg-hover"
              >
                <LeadStatusPill status={option} size="xs">
                  {t(`status.${option}`)}
                </LeadStatusPill>
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
